/**
 * BrandQR — renders a TOTP QR code in Elreign's idiom: rounded "dot" modules
 * framed in the brand primary colour, with the brand mark overlaid at the
 * centre, instead of the raw hard-square image Supabase returns.
 *
 * Drawn from the `otpauth://` URI at ERROR-CORRECTION LEVEL H (the highest, ~30%
 * redundancy). That is what lets a centre logo overlay ~20% of the code without
 * breaking scannability — the same technique every centre-logo QR uses. The
 * level is asserted by the component test; do NOT lower it to L/M or the overlay
 * will corrupt the code.
 *
 * BRAND-AWARE (W4, Paul walk 2026-07-13): the centre mark is the WHITE-LABEL
 * TENANT'S logo when the app is served skinned (a `logoUrl` is passed), and the
 * Elreign mark otherwise. Both sit on a white pad so the quiet-zone stays clean.
 * The frame border uses the brand primary token (`--brand-primary`, which the
 * white-label runtime sets to the firm's colour; falls back to Elreign teal), so
 * a skinned tenant automatically gets THEIR colour with no extra wiring.
 *
 * `qrcode` is dynamically imported so it stays in its own lazy chunk and never
 * weighs on the eager auth bundle (mirrors components/qr-handoff.tsx).
 */

import { useEffect, useState } from 'react';
import { ReignMark } from '../reign-mark';
import { FirmLogo } from '../firm-logo';
import { FirmMonogram, monogramFor } from '../firm-monogram';
import './auth-controls.css';

interface QrApi {
  create(
    text: string,
    opts: { errorCorrectionLevel: 'H' },
  ): { modules: { size: number; data: Uint8Array } };
}

/** Build the dotted-QR SVG markup from the module matrix. Pure numbers in —
 *  safe to inject. Finders stay as rounded squares (scanner anchors); data
 *  modules become dots; a white centre pad clears the quiet-zone for the
 *  overlaid brand mark (drawn on top in the DOM, not in this SVG). */
function renderDotQR(size: number, data: Uint8Array): string {
  const dark = (r: number, c: number) => data[r * size + c] === 1;
  const finders: Array<[number, number]> = [
    [0, 0],
    [0, size - 7],
    [size - 7, 0],
  ];
  const inFinder = (r: number, c: number) =>
    finders.some(([fr, fc]) => r >= fr && r < fr + 7 && c >= fc && c < fc + 7);

  let p = '';
  for (const [fr, fc] of finders) {
    p += `<rect x="${fc}" y="${fr}" width="7" height="7" rx="2.3" fill="#161310"/>`;
    p += `<rect x="${fc + 1}" y="${fr + 1}" width="5" height="5" rx="1.6" fill="#ffffff"/>`;
    p += `<rect x="${(fc + 2.1).toFixed(2)}" y="${(fr + 2.1).toFixed(2)}" width="2.8" height="2.8" rx="0.9" fill="#161310"/>`;
  }
  const ctr = size / 2;
  // Radius of the cleared white pad behind the centre mark (~13% of the code →
  // ~26% diameter, comfortably larger than the ~20%-wide overlaid mark). EC-H
  // (~30%) tolerates clearing this zone.
  const padR = size * 0.13;
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (inFinder(r, c) || !dark(r, c)) continue;
      // Skip modules under the centre pad — they're covered by the mark anyway.
      const dx = c + 0.5 - ctr;
      const dy = r + 0.5 - ctr;
      if (dx * dx + dy * dy <= padR * padR) continue;
      p += `<circle cx="${(c + 0.5).toFixed(2)}" cy="${(r + 0.5).toFixed(2)}" r="0.42" fill="#161310"/>`;
    }
  }
  // White centre pad (behind the DOM-overlaid brand mark).
  p += `<circle cx="${ctr.toFixed(2)}" cy="${ctr.toFixed(2)}" r="${padR.toFixed(2)}" fill="#ffffff"/>`;
  return `<svg viewBox="0 0 ${size} ${size}" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">${p}</svg>`;
}

/**
 * The firm's initials, for the centre pad when it has no logo (BRD-05).
 *
 * Up to two letters, because the pad is 22% of the code and a third character
 * is unreadable at any realistic size. Joining words are dropped so
 * "Smith and Partners" reads SP rather than SA, and anything that is not a
 * letter (digits, punctuation, "(Pty) Ltd") never starts an initial.
 *
 * Returns '' when the name yields nothing usable, which the caller reads as
 * "there is no firm identity here" and falls back to the Elreign mark.
 *
 * The rule now lives in `components/firm-monogram.tsx` (2 Sep, BRD-05): both
 * sidebars needed the same fallback and were still drawing the Elreign "E".
 * One module, three consumers; re-exported here so nothing that reads
 * `monogramFor` off this file has to move.
 */
export { monogramFor };

export interface BrandQRProps {
  /** The `otpauth://` provisioning URI to encode. */
  uri: string;
  /** White-label tenant logo URL — when set, overlaid at the centre in place of
   *  the Elreign mark. Absent / null → see `fallbackName`. */
  logoUrl?: string | null;
  /** Accessible name for the overlaid tenant logo (e.g. the firm name). */
  logoAlt?: string;
  /**
   * BRD-05: whose code is this, when there is no logo to say so.
   *
   * Paul, M1 §H: if a firm has no logo, show the FIRM'S NAME, never the Elreign
   * "E" placeholder. A firm name here becomes the firm's own monogram on the
   * centre pad. Omit it on Elreign's own screens, where the Elreign mark is the
   * correct and honest answer.
   */
  fallbackName?: string | null;
}

export function BrandQR({ uri, logoUrl, logoAlt, fallbackName }: BrandQRProps) {
  const [markup, setMarkup] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setMarkup(null);
    setFailed(false);
    void (async () => {
      try {
        const mod = (await import('qrcode')) as unknown as { default?: QrApi } & Partial<QrApi>;
        const qr = (mod.default ?? (mod as QrApi)).create(uri, { errorCorrectionLevel: 'H' });
        if (cancelled) return;
        setMarkup(renderDotQR(qr.modules.size, qr.modules.data));
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uri]);

  if (failed) {
    return (
      <div className="brand-qr-fallback">Couldn&rsquo;t render the code. Use the key below.</div>
    );
  }
  if (markup === null) {
    return <div className="brand-qr-skeleton" aria-hidden="true" />;
  }

  // Brand-aware centre mark, in the order BRD-05 sets: the tenant's LOGO, then
  // the tenant's NAME as a monogram, and Elreign's mark only when there is no
  // tenant identity at all. FirmLogo degrades a broken tenant logo URL to the
  // same fallback rather than to a broken-image glyph.
  const initials = fallbackName ? monogramFor(fallbackName) : '';
  // `bare`: initials only, on the badge's own white pad, hidden from AT like the
  // badge itself — the visual the pad has had since BRD-05 first landed here.
  const noLogoMark = initials ? <FirmMonogram name={fallbackName!} size={30} bare /> : <ReignMark size={30} />;
  const centreMark = logoUrl ? (
    <FirmLogo src={logoUrl} alt={logoAlt ?? 'Authenticator logo'} fallback={noLogoMark} />
  ) : (
    noLogoMark
  );

  return (
    <div className="brand-qr-frame" data-testid="brand-qr">
      <div
        className="brand-qr"
        role="img"
        aria-label="Authenticator QR code"
        dangerouslySetInnerHTML={{ __html: markup }}
      />
      <div className="brand-qr-badge" data-testid="brand-qr-badge" aria-hidden="true">
        {centreMark}
      </div>
    </div>
  );
}
